import {
  hookRunDetailUrl,
  hookRunsUrl,
  HOOKS_BULK_DELETE_URL,
  HOOKS_BULK_TOGGLE_URL,
  HOOKS_URL,
  hookToggleUrl,
  hookUrl,
} from "../constants/automation-api-urls";
import type {
  IAgentRunDetail,
  IBulkOperationResult,
  ICreateHookPayload,
  IHook,
  IHookRun,
  IHookRunsListResponse,
  TBulkDeleteHooksPayload,
  TBulkToggleHooksPayload,
} from "../types";
import type {
  IGetHookRunsProps,
  IGetRunDetailProps,
  IUpdateHookProps,
} from "./automation.types";
import type { ApiClient } from "./client";

export class HookApi {
  constructor(private apiClient: ApiClient) {}

  async getHooks(): Promise<IHook[]> {
    const response = await this.apiClient.get<IHook[]>(HOOKS_URL);
    return response.data;
  }

  async getHook(uuid: string): Promise<IHook> {
    const response = await this.apiClient.get<IHook>(hookUrl(uuid));
    return response.data;
  }

  async createHook(data: ICreateHookPayload): Promise<IHook> {
    const response = await this.apiClient.post<IHook>(HOOKS_URL, data);
    return response.data;
  }

  async updateHook({ uuid, data }: IUpdateHookProps): Promise<IHook> {
    const response = await this.apiClient.put<IHook>(hookUrl(uuid), data);
    return response.data;
  }

  async deleteHook(uuid: string): Promise<void> {
    await this.apiClient.delete(hookUrl(uuid));
  }

  async toggleHook(uuid: string): Promise<IHook> {
    const response = await this.apiClient.put<IHook>(hookToggleUrl(uuid));
    return response.data;
  }

  async bulkToggleHooks(
    payload: TBulkToggleHooksPayload,
  ): Promise<IBulkOperationResult> {
    const response = await this.apiClient.put<IBulkOperationResult>(
      HOOKS_BULK_TOGGLE_URL,
      payload,
    );
    return response.data;
  }

  async bulkDeleteHooks(
    payload: TBulkDeleteHooksPayload,
  ): Promise<IBulkOperationResult> {
    const response = await this.apiClient.delete<IBulkOperationResult>(
      HOOKS_BULK_DELETE_URL,
      { data: payload },
    );
    return response.data;
  }

  async getHookRuns({
    uuid,
    page,
    limit,
  }: IGetHookRunsProps): Promise<IHookRunsListResponse> {
    const response = await this.apiClient.get<IHookRunsListResponse>(
      hookRunsUrl({ uuid, page, limit }),
    );
    return response.data;
  }

  // NOTE: NSAI-5266 — per-run detail (the run + its ordered step trace).
  async getHookRunDetail({
    uuid,
    runUuid,
  }: IGetRunDetailProps): Promise<IAgentRunDetail<IHookRun>> {
    const response = await this.apiClient.get<IAgentRunDetail<IHookRun>>(
      hookRunDetailUrl({ uuid, runUuid }),
    );
    return response.data;
  }
}
